// forms (client) - Public forms support (max 2 public per org)
import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography, Grid, Box, CssBaseline, TextField, Select, MenuItem,
  FormControl, InputLabel, Button, Card, CardContent, IconButton,
  Checkbox, FormControlLabel, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, DialogContentText, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Paper, Tabs, Tab, Badge,
  Tooltip, Snackbar, Alert, LinearProgress, Avatar, Divider, InputAdornment,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon,
  Visibility as VisibilityIcon, ContentCopy as CopyIcon, Share as ShareIcon,
  BarChart as StatsIcon, Description as FormIcon, CheckCircle as CheckIcon,
  Schedule as ClockIcon, Search as SearchIcon, Refresh as RefreshIcon,
  Download as DownloadIcon, Close as CloseIcon,
  ArrowUpward as ArrowUpIcon, ArrowDownward as ArrowDownIcon,
  DragIndicator as DragIcon, Assignment as FillIcon,
  Public as PublicIcon, Lock as LockIcon, OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { global_link } from '../Components/Globallink';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

function StatCard({ icon, label, value, color = 'primary.main' }) {
  return (
    <Card sx={{ height: '100%', borderRadius: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: color, width: 40, height: 40 }}>{icon}</Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>{value}</Typography>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

const ADMIN_TABS = ['build', 'list', 'allsubs', 'fill'];
const AGENT_TABS = ['list', 'fill'];

/** Clean public form URL: https://host/form/<uuid> */
function getPublicFormUrl(uuid) {
  try {
    const origin = new URL(global_link).origin;
    return `${origin}/form/${encodeURIComponent(uuid)}`;
  } catch {
    return `${window.location.origin}/form/${encodeURIComponent(uuid)}`;
  }
}

const Forms = () => {
  const api = axios.create({ baseURL: global_link });
  const authToken = process.env.AUTH_TOKEN;
  const isAgent = sessionStorage.getItem('user_level') === 'agent';
  const TABS = isAgent ? AGENT_TABS : ADMIN_TABS;
  const tabIndex = (name) => TABS.indexOf(name);

  const [activeTab, setActiveTab] = useState(0);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const showSnack = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [fields, setFields] = useState([]);
  const [editMode, setEditMode] = useState(false);

  const [formData, setFormData] = useState({});
  const [formUuid, setFormUuid] = useState(null);
  const [formList, setFormList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formSearch, setFormSearch] = useState('');
  const [formPage, setFormPage] = useState(0);
  const [formRowsPerPage, setFormRowsPerPage] = useState(10);
  const [totalForms, setTotalForms] = useState(0);
  const [publicCount, setPublicCount] = useState(0);
  const [maxPublic, setMaxPublic] = useState(2);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formToDelete, setFormToDelete] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareForm, setShareForm] = useState(null);
  const [viewSubmissionOpen, setViewSubmissionOpen] = useState(false);

  const [submissions, setSubmissions] = useState([]);
  const [totalSubmissions, setTotalSubmissions] = useState(0);
  const [subPage, setSubPage] = useState(0);
  const [subRowsPerPage, setSubRowsPerPage] = useState(10);

  const [allSubmissions, setAllSubmissions] = useState([]);
  const [totalAllSubs, setTotalAllSubs] = useState(0);
  const [allSubPage, setAllSubPage] = useState(0);
  const [allSubRowsPerPage, setAllSubRowsPerPage] = useState(10);
  const [subSearch, setSubSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'submitted_at', dir: 'desc' });
  const [selectedSub, setSelectedSub] = useState(null);

  const [statsTotal, setStatsTotal] = useState(0);
  const [statsToday, setStatsToday] = useState(0);

  const getHeaders = () => {
    const token = localStorage.getItem(authToken);
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const fetchForms = useCallback(async (search = formSearch, page = formPage, rows = formRowsPerPage) => {
    try {
      const r = await api.get('/form/getforms', {
        params: { search, limit: rows, offset: page * rows },
        headers: getHeaders(),
        withCredentials: true,
      });
      setFormList(r.data.data || r.data || []);
      setTotalForms(r.data.total ?? (r.data.data ?? r.data).length);
      if (r.data.public_count != null) setPublicCount(r.data.public_count);
      if (r.data.max_public != null) setMaxPublic(r.data.max_public);
    } catch {
      setFormList([]);
      setTotalForms(0);
    }
  }, [formSearch, formPage, formRowsPerPage]);

  const fetchSubmissions = useCallback(async (uuid, page = subPage, rows = subRowsPerPage) => {
    if (!uuid) return;
    try {
      const r = await api.get(`/form/getsubmissions/${uuid}`, {
        params: { limit: rows, offset: page * rows },
        headers: getHeaders(),
        withCredentials: true,
      });
      setSubmissions(r.data.data || r.data || []);
      setTotalSubmissions(r.data.total ?? (r.data.data ?? r.data).length);
    } catch {
      setSubmissions([]);
      setTotalSubmissions(0);
    }
  }, [subPage, subRowsPerPage]);

  const fetchAllSubmissions = useCallback(async (
    search = subSearch,
    page = allSubPage,
    rows = allSubRowsPerPage,
    sort = sortConfig,
  ) => {
    try {
      const r = await api.get('/form/getallsubmissions', {
        params: { search, limit: rows, offset: page * rows, sort_key: sort.key, sort_dir: sort.dir },
        headers: getHeaders(),
        withCredentials: true,
      });
      const data = r.data.data || r.data || [];
      const total = r.data.total ?? data.length;
      setAllSubmissions(data);
      setTotalAllSubs(total);
      setStatsTotal(r.data.grand_total ?? total);
      setStatsToday(r.data.today_total ?? 0);
    } catch {
      setAllSubmissions([]);
      setTotalAllSubs(0);
    }
  }, [subSearch, allSubPage, allSubRowsPerPage, sortConfig]);

  useEffect(() => {
    fetchForms(formSearch, 0, formRowsPerPage);
    if (!isAgent) fetchAllSubmissions(subSearch, 0, allSubRowsPerPage, sortConfig);
  }, []);

  useEffect(() => {
    if (formUuid) fetchSubmissions(formUuid, 0, subRowsPerPage);
  }, [formUuid]);

  // Refresh list / all-submissions (+ stats) whenever those tabs are selected
  useEffect(() => {
    const name = TABS[activeTab];
    if (name === 'list') {
      fetchForms(formSearch, formPage, formRowsPerPage);
    } else if (name === 'allsubs' && !isAgent) {
      fetchAllSubmissions(subSearch, allSubPage, allSubRowsPerPage, sortConfig);
    }
  }, [activeTab]);

  const handleFormPageChange = (_, newPage) => {
    setFormPage(newPage);
    fetchForms(formSearch, newPage, formRowsPerPage);
  };
  const handleFormRowsPerPageChange = (e) => {
    const rows = parseInt(e.target.value, 10);
    setFormRowsPerPage(rows);
    setFormPage(0);
    fetchForms(formSearch, 0, rows);
  };
  const handleFormSearchChange = (e) => {
    const val = e.target.value;
    setFormSearch(val);
    setFormPage(0);
    fetchForms(val, 0, formRowsPerPage);
  };

  const handleSubPageChange = (_, newPage) => {
    setSubPage(newPage);
    fetchSubmissions(formUuid, newPage, subRowsPerPage);
  };
  const handleSubRowsPerPageChange = (e) => {
    const rows = parseInt(e.target.value, 10);
    setSubRowsPerPage(rows);
    setSubPage(0);
    fetchSubmissions(formUuid, 0, rows);
  };

  const handleAllSubPageChange = (_, newPage) => {
    setAllSubPage(newPage);
    fetchAllSubmissions(subSearch, newPage, allSubRowsPerPage, sortConfig);
  };
  const handleAllSubRowsPerPageChange = (e) => {
    const rows = parseInt(e.target.value, 10);
    setAllSubRowsPerPage(rows);
    setAllSubPage(0);
    fetchAllSubmissions(subSearch, 0, rows, sortConfig);
  };
  const handleSubSearchChange = (e) => {
    const val = e.target.value;
    setSubSearch(val);
    setAllSubPage(0);
    fetchAllSubmissions(val, 0, allSubRowsPerPage, sortConfig);
  };
  const handleSort = (key) => {
    const newSort = { key, dir: sortConfig.key === key && sortConfig.dir === 'asc' ? 'desc' : 'asc' };
    setSortConfig(newSort);
    setAllSubPage(0);
    fetchAllSubmissions(subSearch, 0, allSubRowsPerPage, newSort);
  };

  const resetBuilder = () => {
    setFormName('');
    setFormDescription('');
    setFields([]);
    setFormData({});
    setEditMode(false);
    setFormUuid(null);
  };

  const handleSaveForm = async () => {
    if (!formName.trim()) {
      showSnack('Form name is required', 'warning');
      return;
    }
    setLoading(true);
    const body = { name: formName, description: formDescription, fields };
    try {
      if (editMode && formUuid) {
        await api.put(`/form/updateform/${formUuid}`, body, { headers: getHeaders(), withCredentials: true });
        showSnack('Form updated successfully!');
      } else {
        const r = await api.post('/form/addform', body, { headers: getHeaders(), withCredentials: true });
        setFormUuid(r.data.uuid);
        showSnack('Form saved successfully!');
      }
      resetBuilder();
      fetchForms(formSearch, formPage, formRowsPerPage);
      if (!isAgent) fetchAllSubmissions(subSearch, allSubPage, allSubRowsPerPage, sortConfig);
      setActiveTab(tabIndex('list'));
    } catch (e) {
      showSnack('Failed to save: ' + (e.response?.data?.error || e.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadForm = async (uuid) => {
    if (!uuid) return;
    setLoading(true);
    try {
      const r = await api.get(`/form/getform/${uuid}`, { headers: getHeaders(), withCredentials: true });
      setFields(r.data.fields || r.data || []);
      setFormUuid(uuid);
      setFormData({});
      setSubPage(0);
    } catch (e) {
      showSnack('Failed to load form: ' + (e.response?.data?.error || e.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditForm = async (form) => {
    setLoading(true);
    try {
      const r = await api.get(`/form/getform/${form.uuid}`, {
        headers: getHeaders(),
        withCredentials: true,
      });
      setFields(r.data?.fields || r.data || []);
      setFormUuid(form.uuid);
      setFormName(form.name);
      setFormDescription(form.description || '');
      setFormData({});
      setEditMode(true);
      setActiveTab(tabIndex('build'));
    } catch (err) {
      console.error('Edit load error:', err);
      showSnack('Failed to load form: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePublic = async (form, makePublic) => {
    if (makePublic && publicCount >= maxPublic && !form.is_public) {
      showSnack(`Limit: only ${maxPublic} public forms per organization. Unpublish another first.`, 'warning');
      return;
    }
    setLoading(true);
    try {
      const r = await api.patch(
        `/form/setpublic/${form.uuid}`,
        { is_public: makePublic },
        { headers: getHeaders(), withCredentials: true }
      );
      if (r.data.public_count != null) setPublicCount(r.data.public_count);
      showSnack(makePublic ? 'Form is now public' : 'Form is now private');
      fetchForms(formSearch, formPage, formRowsPerPage);
      if (shareForm?.uuid === form.uuid) {
        setShareForm({ ...shareForm, is_public: makePublic ? 1 : 0 });
      }
    } catch (e) {
      showSnack(e.response?.data?.error || 'Failed to update public status', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteForm = async () => {
    if (!formToDelete) return;
    setLoading(true);
    try {
      await api.delete(`/form/deleteform/${formToDelete.uuid}`, { headers: getHeaders(), withCredentials: true });
      showSnack('Form deleted!');
      fetchForms(formSearch, formPage, formRowsPerPage);
      if (!isAgent) fetchAllSubmissions(subSearch, allSubPage, allSubRowsPerPage, sortConfig);
      if (formToDelete.uuid === formUuid) {
        setFormUuid(null);
        setFields([]);
        setFormData({});
      }
    } catch {
      showSnack('Failed to delete form', 'error');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setFormToDelete(null);
    }
  };

  const handleSubmit = async () => {
    if (!formUuid) {
      showSnack('No form selected', 'warning');
      return;
    }
    const missing = fields.filter((f) => f.required && !formData[f.label]);
    if (missing.length) {
      showSnack(`Required: ${missing.map((f) => f.label).join(', ')}`, 'warning');
      return;
    }
    setLoading(true);
    try {
      await api.post(
        '/form/addforminfo',
        { form_id: formUuid, ...formData },
        { headers: getHeaders(), withCredentials: true }
      );
      showSnack('Form submitted successfully!');
      setFormData({});
      fetchSubmissions(formUuid, subPage, subRowsPerPage);
      if (!isAgent) fetchAllSubmissions(subSearch, allSubPage, allSubRowsPerPage, sortConfig);
    } catch (e) {
      showSnack('Failed to submit: ' + (e.response?.data?.error || e.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  const addField = () =>
    setFields([...fields, { label: '', type: 'text', options: [], subOptions: {}, required: false }]);
  const removeField = (i) => {
    const u = [...fields];
    u.splice(i, 1);
    setFields(u);
  };
  const moveField = (i, dir) => {
    const u = [...fields];
    const s = i + dir;
    if (s < 0 || s >= u.length) return;
    [u[i], u[s]] = [u[s], u[i]];
    setFields(u);
  };
  const updateField = (i, key, val) => {
    const u = [...fields];
    u[i][key] = val;
    setFields(u);
  };
  const handleOptionsChange = (i, str) => {
    const opts = [
      ...new Set(
        (str.match(/"[^"]*"|[^,]+/g) || [])
          .map((o) => {
            let t = o.trim();
            return t.startsWith('"') && t.endsWith('"') ? t.slice(1, -1) : t;
          })
          .filter(Boolean)
      ),
    ];
    updateField(i, 'options', opts);
  };
  const handleSubOptionsChange = (i, parent, str) => {
    const subs = [
      ...new Set(
        (str.match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g) || [])
          .map((o) => {
            let t = o.trim();
            return t.startsWith('"') && t.endsWith('"') ? t.slice(1, -1) : t;
          })
          .filter(Boolean)
      ),
    ];
    const u = [...fields];
    if (!u[i].subOptions) u[i].subOptions = {};
    u[i].subOptions[parent] = subs;
    setFields(u);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const updated = { ...formData, [name]: type === 'checkbox' ? checked : value };
    fields
      .filter((f) => f.type === 'subselect' && f.parentField === name)
      .forEach((f) => {
        updated[f.label] = '';
      });
    setFormData(updated);
  };

  const getParentSelectFields = () => fields.filter((f) => f.type === 'select' && f.label);

  const renderFormField = (field, index) => {
    const common = {
      key: index,
      fullWidth: true,
      label: field.label,
      name: field.label,
      value: formData[field.label] || '',
      onChange: handleInputChange,
      required: field.required,
      margin: 'normal',
    };
    switch (field.type) {
      case 'text':
        return <TextField {...common} />;
      case 'textarea':
        return <TextField {...common} multiline rows={4} />;
      case 'email':
        return <TextField {...common} type="email" />;
      case 'number':
        return <TextField {...common} type="number" />;
      case 'date':
        return <TextField {...common} type="date" InputLabelProps={{ shrink: true }} />;
      case 'select':
        return (
          <FormControl key={index} fullWidth margin="normal">
            <InputLabel>
              {field.label}
              {field.required && ' *'}
            </InputLabel>
            <Select name={field.label} value={formData[field.label] || ''} onChange={handleInputChange}>
              {(field.options || []).map((o, i) => (
                <MenuItem key={i} value={o}>
                  {o}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'subselect': {
        const parentVal = formData[field.parentField];
        const opts = fields.find((f) => f.label === field.parentField)?.subOptions?.[parentVal] || [];
        return (
          <FormControl key={index} fullWidth margin="normal">
            <InputLabel>
              {field.label}
              {field.required && ' *'}
            </InputLabel>
            <Select
              name={field.label}
              value={formData[field.label] || ''}
              onChange={handleInputChange}
              disabled={!parentVal}
            >
              {opts.map((o, i) => (
                <MenuItem key={i} value={o}>
                  {o}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }
      case 'checkbox':
        return (
          <FormControlLabel
            key={index}
            control={
              <Checkbox name={field.label} checked={!!formData[field.label]} onChange={handleInputChange} />
            }
            label={field.label}
            sx={{ mt: 1, display: 'block' }}
          />
        );
      default:
        return null;
    }
  };

  const formatFieldValue = (value, type) => {
    if (value === undefined || value === null || value === '') return '';
    switch (type) {
      case 'date':
        return new Date(value).toLocaleDateString();
      case 'datetime':
        return new Date(value).toLocaleString();
      case 'checkbox':
        return value ? '✓ Yes' : '✗ No';
      case 'select':
      case 'radio':
        return Array.isArray(value) ? value.join(', ') : String(value);
      default:
        return String(value);
    }
  };

  const handleCopyLink = (uuid) => {
    navigator.clipboard.writeText(getPublicFormUrl(uuid));
    showSnack('Public link copied!');
  };

  const exportCSV = () => {
    if (!submissions.length) return;
    const headers = ['ID', 'Form', 'Owner', 'Submitted At', ...fields.map((f) => f.label)];
    const rows = submissions.map((s) => [
      s.id,
      s.form_name,
      s.user_name,
      new Date(s.submitted_at).toLocaleString(),
      ...fields.map((f) => s.submission_data?.[f.label] ?? ''),
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `submissions_${formUuid}.csv`;
    a.click();
  };

  const SortArrow = ({ col }) =>
    sortConfig.key !== col ? null : sortConfig.dir === 'asc' ? (
      <ArrowUpIcon sx={{ fontSize: 13 }} />
    ) : (
      <ArrowDownIcon sx={{ fontSize: 13 }} />
    );

  const renderFormsList = () => (
    <Box sx={{ bgcolor: 'background.paper', boxShadow: 1, borderRadius: 1 }}>
      <Box sx={{ padding: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search forms…"
          value={formSearch}
          onChange={handleFormSearchChange}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 220 }}
        />
        <Button
          startIcon={<RefreshIcon />}
          onClick={() => fetchForms(formSearch, formPage, formRowsPerPage)}
          size="small"
          variant="outlined"
        >
          Refresh
        </Button>
        {!isAgent && (
          <Chip
            size="small"
            icon={<PublicIcon />}
            label={`Public ${publicCount}/${maxPublic}`}
            color={publicCount >= maxPublic ? 'warning' : 'default'}
            variant="outlined"
          />
        )}
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead sx={{ bgcolor: 'background.table' }}>
            <TableRow>
              <TableCell>Form Name</TableCell>
              <TableCell>Description</TableCell>
              {!isAgent && <TableCell align="center">Public</TableCell>}
              {!isAgent && <TableCell align="center">Submissions</TableCell>}
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {formList.length > 0 ? (
              formList.map((form) => (
                <TableRow key={form.uuid} hover>
                  <TableCell>
                    <Typography fontWeight={600}>{form.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {form.description || '—'}
                    </Typography>
                  </TableCell>
                  {!isAgent && (
                    <TableCell align="center">
                      <Tooltip
                        title={
                          form.is_public
                            ? 'Public — anyone with the link can submit'
                            : publicCount >= maxPublic
                              ? `Limit of ${maxPublic} public forms reached`
                              : 'Make public to share externally'
                        }
                      >
                        <Switch
                          size="small"
                          checked={!!form.is_public}
                          onChange={(e) => handleTogglePublic(form, e.target.checked)}
                          color="success"
                        />
                      </Tooltip>
                      {!!form.is_public && (
                        <Chip size="small" label="Live" color="success" sx={{ ml: 0.5 }} />
                      )}
                    </TableCell>
                  )}
                  {!isAgent && (
                    <TableCell align="center">
                      <Chip
                        label={form.submission_count ?? 0}
                        size="small"
                        color={(form.submission_count ?? 0) > 0 ? 'success' : 'default'}
                      />
                    </TableCell>
                  )}
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      <Tooltip title="Fill Form">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            handleLoadForm(form.uuid);
                            setActiveTab(tabIndex('fill'));
                          }}
                        >
                          <FillIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!isAgent && (
                        <>
                          <Tooltip title="Edit Form">
                            <IconButton size="small" color="warning" onClick={() => handleEditForm(form)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Share public link">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => {
                                setShareForm(form);
                                setShareDialogOpen(true);
                              }}
                            >
                              <ShareIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View Submissions">
                            <IconButton
                              size="small"
                              onClick={() => {
                                handleLoadForm(form.uuid);
                                setActiveTab(tabIndex('fill'));
                              }}
                            >
                              <StatsIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Form">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setFormToDelete(form);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={isAgent ? 3 : 5} align="center">
                  {isAgent ? 'No forms available yet.' : 'No forms found. Create one in the Build tab.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalForms}
        page={formPage}
        onPageChange={handleFormPageChange}
        rowsPerPage={formRowsPerPage}
        onRowsPerPageChange={handleFormRowsPerPageChange}
      />
    </Box>
  );

  const renderFillPanel = () =>
    !formUuid ? (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <FillIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography color="text.secondary">Select a form from the Forms tab to fill it out.</Typography>
        <Button variant="outlined" sx={{ mt: 2 }} onClick={() => setActiveTab(tabIndex('list'))}>
          Go to Forms
        </Button>
      </Paper>
    ) : (
      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Fill Form
              </Typography>
              {fields.length > 0 ? (
                <Box>
                  {fields.map((field, i) => renderFormField(field, i))}
                  <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2 }}
                    disabled={loading}
                    fullWidth
                  >
                    {loading ? 'Submitting…' : 'Submit'}
                  </Button>
                </Box>
              ) : (
                <Typography color="text.secondary">No fields in this form.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={7}>
          <Box sx={{ bgcolor: 'background.paper', boxShadow: 1, borderRadius: 1 }}>
            <Box sx={{ padding: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Submissions</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {!isAgent && (
                  <Tooltip title="Export CSV">
                    <span>
                      <IconButton size="small" onClick={exportCSV} disabled={!submissions.length}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                <Tooltip title="Refresh">
                  <IconButton size="small" onClick={() => fetchSubmissions(formUuid, subPage, subRowsPerPage)}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: 'background.table' }}>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Submitted At</TableCell>
                    <TableCell>Owner</TableCell>
                    <TableCell align="center">View</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {submissions.length > 0 ? (
                    submissions.map((sub) => (
                      <TableRow key={sub.id} hover>
                        <TableCell>{sub.id}</TableCell>
                        <TableCell>{new Date(sub.submitted_at).toLocaleString()}</TableCell>
                        <TableCell>{sub.user_name || '—'}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              setSelectedSub(sub);
                              setViewSubmissionOpen(true);
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No submissions yet for this form.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalSubmissions}
              page={subPage}
              onPageChange={handleSubPageChange}
              rowsPerPage={subRowsPerPage}
              onRowsPerPageChange={handleSubRowsPerPageChange}
            />
          </Box>
        </Grid>
      </Grid>
    );

  return (
    <>
      <CssBaseline />
      {loading && <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }} />}
      <Typography variant="h5">{isAgent ? 'Forms' : 'Dynamic Form Builder'}</Typography>
      {!isAgent && (
        <Grid container spacing={2} sx={{ mb: 3, mt: 1 }}>
          <Grid item xs={12} sm={3}>
            <StatCard icon={<FormIcon fontSize="small" />} label="Total Forms" value={totalForms} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <StatCard
              icon={<PublicIcon fontSize="small" />}
              label={`Public forms (max ${maxPublic})`}
              value={`${publicCount}/${maxPublic}`}
              color="info.main"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <StatCard
              icon={<CheckIcon fontSize="small" />}
              label="Total Submissions"
              value={statsTotal}
              color="success.main"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <StatCard
              icon={<ClockIcon fontSize="small" />}
              label="Submitted Today"
              value={statsToday}
              color="warning.main"
            />
          </Grid>
        </Grid>
      )}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }}
      >
        {!isAgent && <Tab label="➕ Build Form" />}
        {!isAgent ? (
          <Tab label={<Badge badgeContent={totalForms} color="primary" sx={{ pr: 1 }}>Forms</Badge>} />
        ) : (
          <Tab label="Forms" />
        )}
        {!isAgent && (
          <Tab
            label={
              <Badge badgeContent={totalAllSubs} color="success" sx={{ pr: 1 }}>
                All Submissions
              </Badge>
            }
          />
        )}
        <Tab label="Fill & Submit" disabled={!formUuid} sx={{ opacity: formUuid ? 1 : 0.4 }} />
      </Tabs>

      {!isAgent && (
        <TabPanel value={activeTab} index={tabIndex('build')}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">{editMode ? 'Edit Form' : 'New Form'}</Typography>
                    {editMode && (
                      <Button size="small" color="inherit" onClick={resetBuilder}>
                        Cancel Edit
                      </Button>
                    )}
                  </Box>
                  <TextField
                    fullWidth
                    label="Form Name *"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Description"
                    value={formDescription}
                    onChange={(e) => setFormDescription(e.target.value)}
                    margin="normal"
                    multiline
                    rows={2}
                  />
                  <Button startIcon={<AddIcon />} onClick={addField} variant="outlined" size="small" sx={{ mt: 2, mb: 1 }}>
                    Add Field
                  </Button>
                  {fields.map((field, index) => (
                    <Card key={index} variant="outlined" sx={{ mt: 1.5, bgcolor: 'background.default' }}>
                      <CardContent sx={{ pb: '12px !important' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                          <DragIcon sx={{ color: 'text.disabled', fontSize: 18 }} />
                          <Typography variant="caption" color="text.secondary">
                            Field {index + 1}
                          </Typography>
                          <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Move up">
                              <span>
                                <IconButton size="small" onClick={() => moveField(index, -1)} disabled={index === 0}>
                                  <ArrowUpIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Move down">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => moveField(index, 1)}
                                  disabled={index === fields.length - 1}
                                >
                                  <ArrowDownIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Remove">
                              <IconButton size="small" color="error" onClick={() => removeField(index)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Field Label"
                              value={field.label}
                              onChange={(e) => updateField(index, 'label', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Type</InputLabel>
                              <Select
                                value={field.type}
                                label="Type"
                                onChange={(e) => updateField(index, 'type', e.target.value)}
                              >
                                <MenuItem value="text">Text</MenuItem>
                                <MenuItem value="textarea">Text Area</MenuItem>
                                <MenuItem value="email">Email</MenuItem>
                                <MenuItem value="number">Number</MenuItem>
                                <MenuItem value="date">Date</MenuItem>
                                <MenuItem value="select">Select (Dropdown)</MenuItem>
                                <MenuItem value="subselect">Sub-Select</MenuItem>
                                <MenuItem value="checkbox">Checkbox</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          {field.type === 'select' && (
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                size="small"
                                label='Options (comma separated; use "quotes" for options with commas)'
                                value={
                                  field.options?.map((o) => (o.includes(',') ? `"${o}"` : o)).join(', ') || ''
                                }
                                onChange={(e) => handleOptionsChange(index, e.target.value)}
                              />
                              {field.options?.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  {field.options.map((opt, oi) => (
                                    <Box key={oi} sx={{ mb: 1.5 }}>
                                      <Chip label={opt} size="small" sx={{ mr: 1, mb: 0.5 }} />
                                      <TextField
                                        fullWidth
                                        size="small"
                                        label={`Sub-options for "${opt}"`}
                                        value={
                                          field.subOptions?.[opt]
                                            ?.map((o) => (o.includes(',') ? `"${o}"` : o))
                                            .join(', ') || ''
                                        }
                                        onChange={(e) => handleSubOptionsChange(index, opt, e.target.value)}
                                      />
                                    </Box>
                                  ))}
                                </Box>
                              )}
                            </Grid>
                          )}
                          {field.type === 'subselect' && (
                            <Grid item xs={12}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Parent Select Field</InputLabel>
                                <Select
                                  value={field.parentField || ''}
                                  label="Parent Select Field"
                                  onChange={(e) => updateField(index, 'parentField', e.target.value)}
                                >
                                  {getParentSelectFields().map((pf, pi) => (
                                    <MenuItem key={pi} value={pf.label}>
                                      {pf.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                          )}
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={!!field.required}
                                  onChange={(e) => updateField(index, 'required', e.target.checked)}
                                />
                              }
                              label={<Typography variant="body2">Required field</Typography>}
                            />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                  <Button
                    onClick={handleSaveForm}
                    variant="contained"
                    color={editMode ? 'warning' : 'success'}
                    fullWidth
                    sx={{ mt: 2 }}
                    disabled={loading || !formName.trim()}
                  >
                    {loading ? 'Saving…' : editMode ? 'Update Form' : 'Save Form'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={5}>
              <Card sx={{ position: 'sticky', top: 80 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Live Preview
                  </Typography>
                  {fields.length === 0 ? (
                    <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      Add fields on the left to preview.
                    </Typography>
                  ) : (
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {formName || 'Untitled Form'}
                      </Typography>
                      {formDescription && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {formDescription}
                        </Typography>
                      )}
                      <Divider sx={{ mb: 1 }} />
                      {fields.map((field, i) => renderFormField(field, i))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      )}

      <TabPanel value={activeTab} index={tabIndex('list')}>
        {renderFormsList()}
      </TabPanel>

      {!isAgent && (
        <TabPanel value={activeTab} index={tabIndex('allsubs')}>
          <Box sx={{ bgcolor: 'background.paper', boxShadow: 1, borderRadius: 1 }}>
            <Box sx={{ padding: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <TextField
                label="Search Submissions"
                variant="outlined"
                size="small"
                value={subSearch}
                onChange={handleSubSearchChange}
                placeholder="Search"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                startIcon={<RefreshIcon />}
                onClick={() => fetchAllSubmissions(subSearch, allSubPage, allSubRowsPerPage, sortConfig)}
                size="small"
                variant="outlined"
              >
                Refresh
              </Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: 'background.table' }}>
                  <TableRow>
                    {[
                      ['id', 'ID'],
                      ['form_name', 'Form'],
                      ['user_name', 'Owner'],
                      ['form_description', 'Description'],
                      ['submitted_at', 'Submitted At'],
                    ].map(([key, label]) => (
                      <TableCell key={key} onClick={() => handleSort(key)} sx={{ cursor: 'pointer', userSelect: 'none' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {label}
                          <SortArrow col={key} />
                        </Box>
                      </TableCell>
                    ))}
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allSubmissions.length > 0 ? (
                    allSubmissions.map((sub) => (
                      <TableRow key={sub.id} hover>
                        <TableCell>{sub.id}</TableCell>
                        <TableCell>{sub.form_name}</TableCell>
                        <TableCell>{sub.user_name || '—'}</TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                            {sub.form_description || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>{new Date(sub.submitted_at).toLocaleString()}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={async () => {
                                if (sub.form_id && sub.form_id !== formUuid) await handleLoadForm(sub.form_id);
                                setSelectedSub(sub);
                                setViewSubmissionOpen(true);
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No submissions yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalAllSubs}
              page={allSubPage}
              onPageChange={handleAllSubPageChange}
              rowsPerPage={allSubRowsPerPage}
              onRowsPerPageChange={handleAllSubRowsPerPageChange}
            />
          </Box>
        </TabPanel>
      )}

      <TabPanel value={activeTab} index={tabIndex('fill')}>
        {renderFillPanel()}
      </TabPanel>

      <Dialog open={viewSubmissionOpen} onClose={() => setViewSubmissionOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Submission #{selectedSub?.id} | By: {selectedSub?.user_name || 'Public Visitor'}
          <IconButton onClick={() => setViewSubmissionOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedSub && (
            <Box>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Form
                  </Typography>
                  <Typography fontWeight={600}>{selectedSub.form_name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Submitted At
                  </Typography>
                  <Typography>{new Date(selectedSub.submitted_at).toLocaleString()}</Typography>
                </Grid>
              </Grid>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Responses
              </Typography>
              <Grid container spacing={2}>
                {fields.map((field) => {
                  const value = selectedSub.submission_data?.[field.label];
                  const isEmpty = value === undefined || value === null || value === '';
                  return (
                    <Grid item xs={12} sm={6} key={field.label}>
                      <Card variant="outlined">
                        <CardContent sx={{ pb: '12px !important' }}>
                          <Typography variant="caption" color="text.secondary">
                            {field.label}
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              fontStyle: isEmpty ? 'italic' : 'normal',
                              color: isEmpty ? 'text.disabled' : 'text.primary',
                            }}
                          >
                            {isEmpty ? 'Not provided' : formatFieldValue(value, field.type)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewSubmissionOpen(false)} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Share — {shareForm?.name}
          <IconButton onClick={() => setShareDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {!shareForm?.is_public ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This form is <strong>private</strong>. Turn on Public in the Forms list (max {maxPublic} per
              organization) before sharing, or enable it below.
              <Box sx={{ mt: 1 }}>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<PublicIcon />}
                  disabled={publicCount >= maxPublic}
                  onClick={() => handleTogglePublic(shareForm, true)}
                >
                  Make public
                </Button>
              </Box>
            </Alert>
          ) : (
            <Alert severity="success" sx={{ mb: 2 }}>
              This form is public. Anyone with the link can submit without logging in.
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Public link (opens standalone form page):
          </Typography>
          <TextField
            fullWidth
            value={shareForm ? getPublicFormUrl(shareForm.uuid) : ''}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Copy">
                    <IconButton onClick={() => handleCopyLink(shareForm?.uuid)}>
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareDialogOpen(false)}>Close</Button>
          {shareForm?.is_public && (
            <Button
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              href={shareForm ? getPublicFormUrl(shareForm.uuid) : undefined}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<CopyIcon />}
            onClick={() => handleCopyLink(shareForm?.uuid)}
            disabled={!shareForm?.is_public}
          >
            Copy Link
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete <strong>&quot;{formToDelete?.name}&quot;</strong>? All submissions will be permanently removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteForm} color="error" variant="contained" disabled={loading}>
            {loading ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Forms;